import api from '../api/apiClient';
import { AxiosError } from 'axios';

export interface Conversation {
  _id: string;
  participants: string[];
  type: 'appointment' | 'manager' | 'support';
  appointment?: string;
  lastMessage?: Message | null;
  tags?: string[];
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  status?: 'open' | 'in-progress' | 'resolved' | 'closed';
  metrics?: {
    firstResponseTime?: string;
    firstResponseDuration?: number;
    resolutionTime?: string;
    resolutionDuration?: number;
    responseCount?: number;
    agentResponseCount?: number;
  };
  assignedTo?: string;
  resolutionNotes?: string;
  resolvedAt?: string;
  resolvedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface MessageAttachment {
  type: 'image' | 'video';
  url: string;
  thumbnailUrl?: string;
  width?: number;
  height?: number;
  duration?: number;
}

export interface Message {
  _id: string;
  conversation: string;
  sender: string;
  content: string;
  attachments?: MessageAttachment[];
  readBy: string[];
  deliveredAt?: string | null;
  readAt?: string | null;
  editedAt?: string | null;
  deletedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  _localStatus?: 'uploading' | 'failed' | 'sending';
  _localUri?: string;
  _failedFile?: File;
}

/**
 * Get all conversations for the authenticated user.
 * GET /messages/conversations
 */
export async function getConversations(): Promise<Conversation[]> {
  try {
    const response = await api.getWithParams<{ data: Conversation[] }>('/messages/conversations');
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Error fetching conversations:', err.response?.data || err.message);
    throw err;
  }
}

/**
 * Create or retrieve a conversation
 * POST /messages/conversations
 */
export async function createConversation(
  participants: string[],
  type: 'appointment' | 'manager' | 'support',
  appointment?: string
): Promise<Conversation> {
  try {
    const response = await api.postWithData<{ data: Conversation }>('/messages/conversations', {
      participants,
      type,
      appointment,
    });
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Error creating conversation:', err.response?.data || err.message);
    throw err;
  }
}

export interface PaginatedMessages {
  conversation: Conversation;
  messages: Message[];
  hasMore: boolean;
  limit: number;
}

export interface GetConversationOptions {
  before?: string;
  limit?: number;
}

/**
 * Get a single conversation and its messages
 * GET /messages/conversations/:conversationId
 * Messages are returned newest-first. Use `before` + `hasMore` to load older pages.
 */
export async function getConversation(
  conversationId: string,
  options: GetConversationOptions = {}
): Promise<PaginatedMessages> {
  try {
    const response = await api.getWithParams<{ data: PaginatedMessages }>(
      `/messages/conversations/${conversationId}`,
      {
        ...(options.before ? { before: options.before } : {}),
        ...(options.limit != null ? { limit: options.limit } : {}),
      }
    );
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error(`Error fetching conversation ${conversationId}:`, err.response?.data || err.message);
    throw err;
  }
}

/**
 * Get all support conversations (for admins)
 * GET /messages/conversations/support
 */
export async function getSupportConversations(): Promise<Conversation[]> {
  try {
    const response = await api.getWithParams<{ data: Conversation[] }>('/messages/conversations/support');
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Error fetching support conversations:', err.response?.data || err.message);
    throw err;
  }
}

/**
 * Send a message in a conversation
 * POST /messages/conversations/:conversationId/messages
 */
export async function sendMessage(
  conversationId: string,
  content: string,
  attachments?: MessageAttachment[]
): Promise<Message> {
  try {
    const response = await api.postWithData<{ data: Message }>(
      `/messages/conversations/${conversationId}/messages`,
      {
        content: content || '',
        ...(attachments?.length ? { attachments } : {}),
      }
    );
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Error sending message:', err.response?.data || err.message);
    throw err;
  }
}

/**
 * Mark a message as read
 * PUT /messages/messages/:messageId/read
 */
export async function markMessageAsRead(messageId: string): Promise<Message> {
  try {
    const response = await api.putWithData<{ data: Message }>(
      `/messages/messages/${messageId}/read`,
      {}
    );
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Error marking message as read:', err.response?.data || err.message);
    throw err;
  }
}

/**
 * Edit a text message within the sender-only edit window.
 * PATCH /messages/messages/:messageId
 */
export async function editMessage(messageId: string, content: string): Promise<Message> {
  try {
    const response = await api.patchWithData<{ data: Message }>(
      `/messages/messages/${messageId}`,
      { content }
    );
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Error editing message:', err.response?.data || err.message);
    throw err;
  }
}

/**
 * Soft-delete a message within the sender-only delete window.
 * DELETE /messages/messages/:messageId
 */
export async function deleteMessage(messageId: string): Promise<Message> {
  try {
    const response = await api.delete(`/messages/messages/${messageId}`);
    return (response.data as { data: Message }).data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Error deleting message:', err.response?.data || err.message);
    throw err;
  }
}

/**
 * Get all conversations with unread counts
 * GET /messages/conversations?includeUnreadCounts=true
 */
export async function getConversationsWithUnreadCounts(): Promise<(Conversation & { unreadCount?: number })[]> {
  try {
    const response = await api.getWithParams<{ data: (Conversation & { unreadCount?: number })[] }>(
      '/messages/conversations?includeUnreadCounts=true'
    );
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Error fetching conversations with unread counts:', err.response?.data || err.message);
    throw err;
  }
}

/**
 * Get total unread message count across all conversations
 * GET /messages/conversations/unread-count
 */
export async function getTotalUnreadCount(): Promise<number> {
  try {
    const response = await api.getWithParams<{ data: { totalUnread: number } }>(
      '/messages/conversations/unread-count'
    );
    return response.data.data.totalUnread;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Error fetching total unread count:', err.response?.data || err.message);
    throw err;
  }
}

/**
 * Update conversation tags, priority, and status (support agents only).
 * PUT /messages/conversations/:id/tags
 */
export async function updateConversationTags(
  conversationId: string,
  data: {
    tags?: string[];
    priority?: 'low' | 'medium' | 'high' | 'urgent';
    status?: 'open' | 'in-progress' | 'resolved' | 'closed';
    resolutionNotes?: string;
  }
): Promise<Conversation> {
  try {
    const response = await api.putWithData<{ data: Conversation }>(
      `/messages/conversations/${conversationId}/tags`,
      data
    );
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Error updating conversation tags:', err.response?.data || err.message);
    throw err;
  }
}

/**
 * Get support metrics (support agents only).
 * GET /messages/conversations/support/metrics
 */
export async function getSupportMetrics(params?: {
  startDate?: string;
  endDate?: string;
}): Promise<{
  totalConversations: number;
  statusCounts: {
    open: number;
    inProgress: number;
    resolved: number;
    closed: number;
  };
  priorityCounts: {
    low: number;
    medium: number;
    high: number;
    urgent: number;
  };
  tagCounts: Record<string, number>;
  averageMetrics: {
    firstResponseTime: number; // minutes
    resolutionTime: number; // hours
  };
  resolutionRate: number; // percentage
}> {
  try {
    const response = await api.getWithParams<{
      data: {
        totalConversations: number;
        statusCounts: {
          open: number;
          inProgress: number;
          resolved: number;
          closed: number;
        };
        priorityCounts: {
          low: number;
          medium: number;
          high: number;
          urgent: number;
        };
        tagCounts: Record<string, number>;
        averageMetrics: {
          firstResponseTime: number;
          resolutionTime: number;
        };
        resolutionRate: number;
      };
    }>('/messages/conversations/support/metrics', params);
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Error fetching support metrics:', err.response?.data || err.message);
    throw err;
  }
}
